import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { useTheme } from '../theme/ThemeContext';
import { useThemedStyles } from '../theme/useThemedStyles';
import type { Theme } from '../theme/types';

interface FilterTab {
  key: string;
  label: string;
}

interface FilterTabsProps {
  tabs: FilterTab[];
  activeKey: string;
  onSelect: (key: string) => void;
  /** Optional counts to display as badges next to each tab label. Only shown in "tab" variant. */
  counts?: Record<string, number>;
  /** Visual variant. "tab" (default) uses a light highlight; "chip" uses pill-shaped solid-fill chips. */
  variant?: 'tab' | 'chip';
  /** Prefix for testIDs, e.g. "tasks" produces "tasks.filter-tab-today" */
  testIDPrefix?: string;
}

export default function FilterTabs({
  tabs,
  activeKey,
  onSelect,
  counts,
  variant = 'tab',
  testIDPrefix,
}: FilterTabsProps) {
  const tabStyles = useThemedStyles(createTabStyles);
  const chipStyles = useThemedStyles(createChipStyles);

  const makeTestID = (key: string) =>
    testIDPrefix ? `${testIDPrefix}.filter-tab-${key}` : `filter-tab-${key}`;

  if (variant === 'chip') {
    return (
      <View style={chipStyles.filterRow} accessibilityRole="tablist">
        {tabs.map((tab) => {
          const isActive = activeKey === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              style={[chipStyles.filterChip, isActive && chipStyles.filterChipActive]}
              onPress={() => onSelect(tab.key)}
              accessibilityRole="button"
              accessibilityState={{ selected: isActive }}
              accessibilityLabel={tab.label}
              testID={makeTestID(tab.key)}
            >
              <Text
                style={[
                  chipStyles.filterChipText,
                  isActive && chipStyles.filterChipTextActive,
                ]}
                accessible={false}
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  }

  return (
    <View style={tabStyles.filterBar} accessibilityRole="tablist">
      {tabs.map((tab) => {
        const isActive = activeKey === tab.key;
        const count = counts?.[tab.key];
        return (
          <TouchableOpacity
            key={tab.key}
            style={[tabStyles.filterTab, isActive && tabStyles.filterTabActive]}
            onPress={() => onSelect(tab.key)}
            accessibilityRole="button"
            accessibilityState={{ selected: isActive }}
            accessibilityLabel={count != null && count > 0 ? `${tab.label}, ${count} items` : tab.label}
            testID={makeTestID(tab.key)}
          >
            <Text
              style={[
                tabStyles.filterTabText,
                isActive && tabStyles.filterTabTextActive,
              ]}
              accessible={false}
            >
              {tab.label}
            </Text>
            {count != null && count > 0 && (
              <View
                style={[
                  tabStyles.filterCount,
                  isActive && tabStyles.filterCountActive,
                ]}
                accessible={false}
              >
                <Text
                  style={[
                    tabStyles.filterCountText,
                    isActive && tabStyles.filterCountTextActive,
                  ]}
                >
                  {count}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const createTabStyles = (theme: Theme) => ({
  filterBar: {
    flexDirection: 'row' as const,
    backgroundColor: theme.colors.surface,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    gap: 4,
  },
  filterTab: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 6,
  },
  filterTabActive: {
    backgroundColor: theme.colors.primaryBg,
  },
  filterTabText: {
    fontSize: 13,
    fontWeight: '600' as const,
    color: theme.colors.textMuted,
  },
  filterTabTextActive: {
    color: theme.colors.primaryDark,
  },
  filterCount: {
    backgroundColor: theme.colors.bgTertiary,
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    paddingHorizontal: 6,
  },
  filterCountActive: {
    backgroundColor: theme.colors.primaryLight,
  },
  filterCountText: {
    fontSize: 11,
    fontWeight: '700' as const,
    color: theme.colors.textDisabled,
  },
  filterCountTextActive: {
    color: theme.colors.primaryDark,
  },
});

const createChipStyles = (theme: Theme) => ({
  filterRow: {
    flexDirection: 'row' as const,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.borderLight,
  },
  filterChip: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: theme.colors.bgTertiary,
  },
  filterChipActive: {
    backgroundColor: theme.colors.primary,
  },
  filterChipText: {
    fontSize: 13,
    fontWeight: '600' as const,
    color: theme.colors.textMuted,
  },
  filterChipTextActive: {
    color: theme.colors.onPrimary,
  },
});
